import React from 'react';
import styled from 'styled-components';
import FooterAttribution from './FooterAttribution';
import { UserContext } from '../contexts';
import { CookiePreferencesTrigger } from '../cookie-banner';
import { GetColor } from 'venn-ui-kit';
import type { UserLanguageEnum } from 'venn-api';
import { cn } from '@/lib/utils';

export interface FooterLink {
  titles: { language: string; text: string }[];
  baseUrl: string;
}

const StyledAttribution = styled(FooterAttribution)`
  padding-top: 10px;
  color: ${GetColor.White};
`;

export const getTitle = (link: FooterLink, inLanguage?: string): string => {
  const title = link.titles.find(({ language }) => language === inLanguage);
  return title ? title.text : link.titles[0]!.text; // if nothing found, use the first one
};

interface LocalizedFooterLinkProps {
  link: FooterLink;
  language?: UserLanguageEnum;
  jurisdiction?: string;
}

export const LocalizedFooterLink: React.FC<React.PropsWithChildren<LocalizedFooterLinkProps>> = ({
  link,
  language,
  jurisdiction,
}) => {
  const linkTarget = jurisdiction ? `${link.baseUrl}?jurisdiction=${jurisdiction}` : link.baseUrl;
  return (
    <a
      href={linkTarget}
      target="_blank"
      rel="noopener noreferrer"
      className={cn(
        'text-venn-primary-main font-bold text-[12px] leading-[16px] text-left',
        'hover:border-b hover:border-solid hover:border-b-venn-primary-main',
        'focus:border-b focus:border-solid focus:border-b-venn-primary-main',
      )}
    >
      {getTitle(link, language)}
    </a>
  );
};

const FooterLinks = () => (
  <nav>
    <ul className="flex gap-[15px] p-0 m-0 list-none">
      <CookiePreferencesTrigger />
    </ul>
  </nav>
);

const Footer: React.FC = () => (
  <footer className="flex flex-col items-center justify-center py-[90px] px-[60px] bg-venn-grey-90 transform-gpu print:hidden">
    <div className="flex flex-col justify-end items-end w-full">
      <FooterLinks />
    </div>
    <div className="flex w-full">
      <UserContext.Consumer>
        {({ profileSettings }) => (
          <StyledAttribution countryCode={profileSettings?.organization?.jurisdiction?.iso2Code} />
        )}
      </UserContext.Consumer>
    </div>
  </footer>
);

export default React.memo(Footer);
